import React, { useState, useCallback, useMemo } from "react";
import { IoPlay, IoPause } from "react-icons/io5";
import {
  Gender,
  s3Dir,
  supportedLanguages,
  topVoices,
  Voice,
} from "../../../../types";
import classNames from "./VoiceAvatar.module.css";
import LanguageSelect from "../../LanguageSelect";
import useUser from "../../../../hooks/user";
import GenderSelect from "./GenderSelect";
import terraImg from "../../../../assets/planets/terra.webp";
import mercuryImg from "../../../../assets/planets/mercury.webp";
import marsImg from "../../../../assets/planets/mars.webp";
import jupiterImg from "../../../../assets/planets/jupiter.webp";
import venusImg from "../../../../assets/planets/venus.webp";
import lunaImg from "../../../../assets/planets/luna.webp";
// import SimpleBar from "simplebar-react";

const planetNames: Record<Gender, string[]> = {
  male: ["mercury", "mars", "jupiter"],
  female: ["terra", "venus", "luna"],
};

const Waveform = () => {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 26 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        className={`${classNames.Wave} ${classNames.WaveDelay1}`}
        d="M3 5V21"
        stroke="#A099FF"
        strokeWidth="2"
      />
      <path
        className={`${classNames.Wave} ${classNames.WaveDelay2}`}
        d="M7 8V18"
        stroke="#A099FF"
        strokeWidth="2"
      />
      <path
        className={`${classNames.Wave} ${classNames.WaveDelay3}`}
        d="M11 10V16"
        stroke="#A099FF"
        strokeWidth="2"
      />
      <path
        className={`${classNames.Wave} ${classNames.WaveDelay4}`}
        d="M15 7V19"
        stroke="#A099FF"
        strokeWidth="2"
      />
      <path
        className={`${classNames.Wave} ${classNames.WaveDelay5}`}
        d="M19 4V22"
        stroke="#A099FF"
        strokeWidth="2"
      />
      <path
        className={`${classNames.Wave} ${classNames.WaveDelay4}`}
        d="M23 7V19"
        stroke="#A099FF"
        strokeWidth="2"
      />
    </svg>
  );
};

export const VoiceList = (props: {
  basic?: boolean;
  language: string;
  gender?: Gender;
  selectedVoice?: Voice;
  onSelectVoice?: (voice: Voice) => void;
}) => {
  const [playingAudioId, setPlayingAudioId] = useState<string>();

  const getVoicesForLocaleAndGender = (lang: string): Voice[] => {
    const voices: Voice[] = [];

    if (props.gender !== "male") {
      // Add female voices
      topVoices.female[lang]?.forEach((_, index) => {
        voices.push({
          id: `female ${index + 1}`,
          rank: index + 1,
          gender: "female",
          previewUrl: `${s3Dir}voiceSamples/female/${lang}/voice${
            index + 1
          }.mp3`,
        });
      });
    }

    if (props.gender !== "female") {
      // Add male voices
      topVoices.male[lang]?.forEach((_, index) => {
        voices.push({
          id: `male ${index + 1}`,
          rank: index + 1,
          gender: "male",
          previewUrl: `${s3Dir}voiceSamples/male/${lang}/voice${index + 1}.mp3`,
        });
      });
    }

    return voices;
  };

  const handlePlayAudio = useCallback(
    (voiceId: string, previewUrl: string, e?: React.MouseEvent) => {
      e?.stopPropagation();

      if (playingAudioId === voiceId) {
        // Stop current audio
        const audio = document.getElementById(
          `audio-${voiceId}`
        ) as HTMLAudioElement;
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
        setPlayingAudioId(undefined);
      } else {
        // Stop previous audio if any
        if (playingAudioId) {
          const prevAudio = document.getElementById(
            `audio-${playingAudioId}`
          ) as HTMLAudioElement;
          if (prevAudio) {
            prevAudio.pause();
            prevAudio.currentTime = 0;
          }
        }

        // Play new audio
        const audio = document.getElementById(
          `audio-${voiceId}`
        ) as HTMLAudioElement;
        if (audio) {
          audio.src = previewUrl;
          audio.play();
          setPlayingAudioId(voiceId);
        }
      }
    },
    [playingAudioId]
  );

  const voices = getVoicesForLocaleAndGender(props.language);

  const getPlanetName = useCallback((planet: string) => {
    switch (planet) {
      case "terra":
        return "Terra";
      case "venus":
        return "Venus";
      case "luna":
        return "Luna";
      case "mercury":
        return "Mercury";
      case "mars":
        return "Mars";
      case "jupiter":
        return "Jupiter";
    }
  }, []);

  const getPlanetImage = useCallback((planet: string) => {
    switch (planet) {
      case "terra":
        return terraImg;
      case "venus":
        return venusImg;
      case "luna":
        return lunaImg;
      case "mercury":
        return mercuryImg;
      case "mars":
        return marsImg;
      case "jupiter":
        return jupiterImg;
    }
  }, []);

  if (voices.length <= 0 || props.basic) {
    return (
      <div className={classNames.VoiceList}>
        <div
          key={"female 1"}
          className={classNames.VoiceItem}
          aria-selected={props.selectedVoice?.gender === "female"}
          onClick={() => {
            const voice: Voice = {
              id: "female 1",
              rank: 1,
              previewUrl: "",
              gender: "female",
            };
            if (props.onSelectVoice) {
              props.onSelectVoice(voice);
            }
          }}
        >
          <div>
            <img
              src={getPlanetImage("terra")}
              alt={getPlanetName("terra")}
              className={classNames.VoiceIcon}
            />
            <span className={classNames.VoiceName}>
              {getPlanetName("terra")}
            </span>
          </div>
        </div>
        <div
          key={"male 1"}
          className={classNames.VoiceItem}
          aria-selected={props.selectedVoice?.gender === "male"}
          onClick={() => {
            const voice: Voice = {
              id: "male 1",
              rank: 1,
              previewUrl: "",
              gender: "male",
            };
            if (props.onSelectVoice) {
              props.onSelectVoice(voice);
            }
          }}
        >
          <div>
            <img
              src={getPlanetImage("mercury")}
              alt={getPlanetName("mercury")}
              className={classNames.VoiceIcon}
            />
            <span className={classNames.VoiceName}>
              {getPlanetName("mercury")}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames.VoiceList}>
      {voices.map((voice) => {
        const planet = planetNames[voice.gender][voice.rank - 1];
        const isSelected = props.selectedVoice?.id === voice.id;

        return (
          <div
            key={voice.id}
            className={classNames.VoiceItem}
            aria-selected={isSelected}
            onClick={() => props.onSelectVoice && props.onSelectVoice(voice)}
          >
            <div>
              <img
                src={getPlanetImage(planet)}
                alt={getPlanetName(planet)}
                className={classNames.VoiceIcon}
              />
              <span className={classNames.VoiceName}>
                {getPlanetName(planet)}
              </span>
            </div>
            <div className={classNames.WaveformAndButton}>
              {playingAudioId === voice.id && <Waveform />}
              <>
                <button
                  className={classNames.PlayButton}
                  onClick={(e) =>
                    handlePlayAudio(voice.id, voice.previewUrl, e)
                  }
                  style={
                    playingAudioId === voice.id
                      ? {
                          backgroundColor: "#a099ff",
                          color: "#161616",
                        }
                      : undefined
                  }
                >
                  {playingAudioId === voice.id ? (
                    <IoPause />
                  ) : (
                    <IoPlay style={{ marginLeft: "2px" }} />
                  )}
                </button>

                <audio
                  id={`audio-${voice.id}`}
                  onEnded={() => setPlayingAudioId(undefined)}
                />
              </>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const VoiceAvatar = (props: {
  // Basic mode -- only male/female selection
  basic?: boolean;
  selectedVoice?: Voice;
  onSelectVoice: (voice: Voice) => void;
}) => {
  const [gender, setGender] = useState<Gender>();

  const [user, setUser] = useUser();
  const languages = useMemo(() => {
    return Object.values(supportedLanguages).flatMap(
      (languages) => Array.from(languages) as string[]
    );
  }, []);
  const [language, setLanguage] = useState(
    languages.includes(user.metadata.selectedLanguage)
      ? user.metadata.selectedLanguage
      : "en-US"
  );

  return (
    <div className={classNames.VoiceAvatar}>
      <div className={classNames.Filters}>
        <LanguageSelect
          languages={languages}
          selectedLanguage={language}
          onChange={setLanguage}
          color="gray"
          backgroundColor="black"
          borderColor="dark-gray"
        />
        <GenderSelect gender={gender} onChange={(value) => setGender(value)} />
      </div>

      {/* <SimpleBar style={{ width: "100%", height: "100%" }}> */}
      <VoiceList
        basic={props.basic}
        language={language}
        gender={gender}
        selectedVoice={props.selectedVoice}
        onSelectVoice={(voice) => {
          if (props.onSelectVoice) {
            props.onSelectVoice(voice);
          }
          setUser((prevState) => ({
            ...prevState,
            metadata: {
              ...prevState.metadata,
              aiVoice: voice.rank.toString(),
              aiVoiceGender: voice.gender,
            },
          }));
        }}
      />
      {/* </SimpleBar> */}
    </div>
  );
};

export default VoiceAvatar;
